import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/modules/Button';
import { Input } from '@/modules/Input';
import { DatePicker } from '@/modules/DatePicker';
import { Separator } from '@/modules/Separator';
import { cn } from '@/shared/utils/cn';
import { ToolbarProps, ToolbarButton, ToolbarAddon } from '../../types';
import { faXmark, faArrowTurnLeft } from '@fortawesome/pro-regular-svg-icons';
import {
  getToolbarActionButtonProps,
  getToolbarAddonButtonProps,
} from '../../utils/toolbarButtonProps';
import { resolveButtonTooltipProps, getResolvedTooltipLabel } from '@/shared/utils/resolveButtonTooltipProps';

/**
 * Toolbar - Barra de herramientas flexible con botones y addons
 *
 * Según diseño de Figma:
 * - Fondo: bg-fill-brand (azul)
 * - Border radius: radius-surface-modal (16px)
 * - Padding: 16px horizontal, 8px vertical
 * - Gap: 16px entre elementos
 * - Texto blanco para el contador
 * - Divisor vertical entre secciones
 *
 * Funcionamiento:
 * 1. Siempre se muestra primero la vista de botones
 * 2. Cada botón puede tener sus propios addons asociados
 * 3. Al hacer click en un botón:
 *    - Se ejecuta onClick (siempre)
 *    - Si tiene addons, se muestra la vista de addons de ese botón
 * 4. En la vista de addons se muestra (por defecto):
 *    - Botón de volver (opcional con `showBackButton={false}`)
 *    - Texto con contador "(count) text"
 *    - Los addons del botón seleccionado
 *    - Botón de cerrar (si `onClose` está definido y `showCloseButton !== false`)
 *
 * Vista de botones — opt-in `showCounterInButtonsView`:
 *    - Contador + separador + botones + separador + cierre (si `onClose`), alineado a diseños tipo selección masiva.
 *
 * Modo controlado (para compartir estado entre múltiples toolbars):
 * - Proporciona activeButtonId y onActiveButtonIdChange para controlar el estado externamente
 * - Los botones deben tener un id único para funcionar correctamente en modo controlado
 * - Si no se proporcionan estas props, el componente funciona en modo no controlado (estado interno)
 */
export const Toolbar = ({
  text,
  count,
  buttons = [],
  onClose,
  className,
  visible = true,
  activeButtonId,
  onActiveButtonIdChange,
  onBackClick,
  showCounterInButtonsView = false,
  showBackButton = true,
  showCloseButton = true,
  chrome,
}: ToolbarProps) => {
  // Determinar si está en modo controlado
  const isControlled = activeButtonId !== undefined;

  // Índice del botón activo (modo no controlado), -1 = vista de botones
  const [activeButtonIndex, setActiveButtonIndex] = useState<number>(-1);
  const [mounted, setMounted] = useState(false);

  // Asegurar que el portal solo se monte en el cliente
  useEffect(() => {
    setMounted(true);
  }, []);

  // Si no es visible, no renderizar
  if (!visible) return null;

  // Filtrar botones que deben mostrarse
  const visibleButtons = buttons.filter((btn) => btn.show !== false);

  // Si no hay botones visibles, no renderizar
  if (visibleButtons.length === 0) return null;

  // Determinar el botón activo según el modo (controlado o no controlado)
  let activeButton: ToolbarButton | null = null;

  if (isControlled) {
    // Modo controlado: buscar el botón por id
    if (activeButtonId !== null) {
      activeButton = visibleButtons.find((btn) => btn.id === activeButtonId) || null;
    }
    // Si activeButtonId es null, activeButton será null (vista de botones)
  } else {
    // Modo no controlado: usar el índice (comportamiento original)
    activeButton = activeButtonIndex >= 0 ? visibleButtons[activeButtonIndex] : null;
  }

  const activeAddons = activeButton?.addons?.filter((addon) => addon.show !== false) || [];

  const hasSelectionSummary = Boolean(text || count !== undefined);
  const selectionSummary = hasSelectionSummary ? (
    <span className="ui-whitespace-nowrap ui-text-body-sm-regular ui-font-body-sm-regular ui-text-on-color">
      {count !== undefined ? `(${count}) ` : ''}
      {text}
    </span>
  ) : null;

  /** Evita un separador inicial huérfano cuando no hay back ni contador antes de los addons. */
  const showSeparatorBeforeAddons =
    activeAddons.length > 0 && (showBackButton || hasSelectionSummary);

  /** X visible solo con callback y sin opt-out explícito (`showCloseButton={false}`). */
  const shouldRenderCloseButton = Boolean(onClose) && showCloseButton;

  const backTooltipProps = resolveButtonTooltipProps({ tooltip: chrome?.back?.tooltip });
  const closeTooltipProps = resolveButtonTooltipProps({ tooltip: chrome?.close?.tooltip });
  const backTooltipLabel = getResolvedTooltipLabel(backTooltipProps);
  const closeTooltipLabel = getResolvedTooltipLabel(closeTooltipProps);

  const baseClasses = cn(
    // Layout base
    'ui-flex ui-items-center ui-gap-8 sm:ui-gap-16',
    // Fondo y estilos
    'ui-bg-fill-brand',
    'ui-px-12 ui-py-8 sm:ui-px-16',
    'ui-rounded-surface-modal',
    'ui-shadow-03',
    'ui-border-1 ui-border-default',
    // Tooltips portaled deben poder salir por encima de la barra
    'ui-overflow-visible',
    className
  );

  const portalContainerClasses = cn(
    // Posicionamiento fijo en la parte inferior, centrado horizontalmente
    'ui-fixed ui-bottom-6 ui-left-1/2 ui--translate-x-1/2',
    // Z-index alto para estar sobre otros elementos (menor que el DatePicker que usa 1100)
    'ui-z-[1000]',
    // En móvil permitir scroll horizontal si es necesario, en desktop ajustar al contenido
    'ui-max-w-[calc(100vw-48px)]',
    // Flex para centrar el contenido
    'ui-flex ui-justify-center'
  );

  const handleButtonClick = (button: ToolbarButton, index: number) => {
    // Siempre ejecutar onClick si existe
    if (button.onClick) {
      button.onClick();
    }
    // Si el botón tiene addons, mostrar la vista de addons de este botón
    const buttonAddons = button.addons?.filter((addon) => addon.show !== false) || [];
    if (buttonAddons.length > 0) {
      // Usar id si existe, sino usar el índice como fallback (convertido a string)
      const buttonId = button.id ?? index.toString();

      if (isControlled) {
        // Modo controlado: llamar al callback
        onActiveButtonIdChange?.(buttonId);
      } else {
        // Modo no controlado: actualizar estado interno
        setActiveButtonIndex(index);
      }
    }
  };

  const handleBackToButtons = () => {
    onBackClick?.();
    if (isControlled) {
      // Modo controlado: llamar al callback con null
      onActiveButtonIdChange?.(null);
    } else {
      // Modo no controlado: actualizar estado interno
      setActiveButtonIndex(-1);
    }
  };

  const handleAddonChange = (
    addon: ToolbarAddon,
    value: string | Date | null | { start: Date | null; end: Date | null }
  ) => {
    if (addon.onChange) {
      addon.onChange(value);
    }
  };

  // Contenido del Toolbar - Vista de addons del botón activo
  const renderAddonsView = () => (
    <div className={baseClasses}>
      {/* Botón para volver a botones (opt-out con showBackButton={false}) */}
      {showBackButton && (
        <Button
          color="secondary"
          size="xs"
          pill="pill"
          icon={faArrowTurnLeft}
          onClick={handleBackToButtons}
          {...backTooltipProps}
          {...(backTooltipLabel ? { pointer: false } : {})}
        />
      )}

      {/* Texto con contador (ej: "(3) Seleccionados") */}
      {selectionSummary}

      {/* Separador entre bloque inicial y addons */}
      {showSeparatorBeforeAddons && (
        <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
      )}

      {/* Addons del botón activo */}
      <div className="ui-flex ui-min-w-max ui-items-center ui-gap-16">
        {activeAddons.map((addon, index) => {
          if (addon.type === 'input') {
            return (
              <div key={`addon-${index}`} className="ui-w-[160px]">
                <Input
                  size="xs"
                  leftSlot={addon.leftSlot}
                  fullWidth={true}
                  rightSlot={addon.rightSlot}
                  disabled={addon.disabled}
                  value={addon.value}
                  placeholder={addon.placeholder}
                  type={addon.type}
                  name={addon.name}
                  onValueChange={(value) => handleAddonChange(addon, value)}
                />
              </div>
            );
          } else if (addon.type === 'date') {
            return (
              <div key={`addon-${index}`} className="ui-w-[160px]">
                <DatePicker
                  size="xs"
                  mode={addon.datePickerProps?.mode || 'single'}
                  selectedDate={addon.selectedDate}
                  selectedRange={addon.selectedRange}
                  minDate={addon.datePickerProps?.minDate}
                  maxDate={addon.datePickerProps?.maxDate}
                  autoCloseRange={false}
                  format={addon.datePickerProps?.format || 'dd/MM/yyyy'}
                  placeholder={
                    addon.datePickerProps?.placeholder || addon.placeholder || 'Seleccionar fecha'
                  }
                  onDateChange={(date) => {
                    if (addon.datePickerProps?.mode === 'range') {
                      handleAddonChange(addon, { start: null, end: null });
                    } else {
                      handleAddonChange(addon, date);
                    }
                  }}
                  onRangeChange={(range) => {
                    handleAddonChange(addon, range);
                  }}
                />
              </div>
            );
          } else if (addon.type === 'button') {
            return (
              <Button
                key={`addon-${index}`}
                {...getToolbarAddonButtonProps(addon, {
                  onClick: () => {
                    addon.onClick?.();
                  },
                })}
              >
                {addon.label}
              </Button>
            );
          }
          return null;
        })}
      </div>

      {/* Botón de cerrar */}
      {shouldRenderCloseButton && (
        <>
          <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
          <Button
            color="secondary"
            size="xs"
            pill="pill"
            icon={faXmark}
            onClick={onClose}
            {...closeTooltipProps}
            {...(closeTooltipLabel ? { pointer: false } : {})}
          />
        </>
      )}
    </div>
  );

  // Contenido del Toolbar - Vista de botones
  const renderButtonsView = () => (
    <div className={baseClasses}>
      {/* Opt-in: contador + separador antes de los botones (diseño tipo Figma / selección) */}
      {showCounterInButtonsView && hasSelectionSummary && (
        <>
          {selectionSummary}
          <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
        </>
      )}

      {/* Botones */}
      <div className="ui-flex ui-min-w-max ui-items-center ui-gap-16">
        {visibleButtons.map((button, index) => (
          <Button
            key={`button-${index}-${button.label || button.icon}`}
            {...getToolbarActionButtonProps(button, {
              onClick: () => handleButtonClick(button, index),
            })}
          >
            {button.label}
          </Button>
        ))}
      </div>

      {/* Botón de cerrar */}
      {shouldRenderCloseButton && (
        <>
          <Separator vertical className="ui-h-8 ui-border-on-color ui-opacity-30" />
          <Button
            color="secondary"
            size="xs"
            pill="pill"
            icon={faXmark}
            onClick={onClose}
            {...closeTooltipProps}
            {...(closeTooltipLabel ? { pointer: false } : {})}
          />
        </>
      )}
    </div>
  );

  // Determinar qué vista renderizar
  const shouldShowAddonsView = isControlled
    ? activeButtonId !== null && activeButton !== null && activeAddons.length > 0
    : activeButtonIndex >= 0 && activeButton !== null && activeAddons.length > 0;

  const toolbarContent = shouldShowAddonsView ? renderAddonsView() : renderButtonsView();

  // Renderizar usando portal si está montado, sino retornar null
  if (!mounted) return null;

  return createPortal(
    <div className={cn('ui-imj', portalContainerClasses)}>{toolbarContent}</div>,
    document.body
  );
};
